<template>
    <transition name="fade">
        <div v-if="show" class="modal-overlay" @click.self="close">
            <div class="modal-container" ref="modal">
                <div class="modal-header">
                    <h2>Log Details</h2>
                    <button class="close-btn" @click="close">&times;</button>
                </div>
                <div class="modal-content">
                    <section class="log-section" v-if="hasUserDetails">
                        <h3>User Details</h3>
                        <p v-if="safeGet(log, 'user_id') !== 'Unknown'">
                            <strong>User ID:</strong> {{ safeGet(log, 'user_id') }}
                        </p>
                        <p v-if="safeGet(log, 'usermail') !== 'Unknown'">
                            <strong>Email:</strong> {{ safeGet(log, 'usermail') }}
                        </p>

                        <div v-if="hasUserAdditionalDetails">
                            <h4 @click="toggleUserAccordion" class="accordion-header">
                                User Additional Details
                                <span>{{ isUserAccordionOpen ? '▲' : '▼' }}</span>
                            </h4>
                            <div v-if="isUserAccordionOpen" class="accordion-content">
                                <p v-if="safeGet(log, 'user_data.phone', safeGet(log, 'userData.phone')) !== 'Unknown'">
                                    <strong>Phone:</strong>
                                    {{ safeGet(log, 'user_data.phone', safeGet(log, 'userData.phone')) }}
                                </p>
                                <p>
                                    <strong>Roles:</strong>
                                    <span
                                        v-if="(log.user_data && log.user_data.roles && log.user_data.roles.length) || (log.userData && log.userData.roles && log.userData.roles.length)">
                                        {{
                                            (log.user_data ? log.user_data.roles : log.userData.roles)
                                                .map(role => role.name)
                                                .join(', ')
                                        }}
                                    </span>
                                    <span v-else>N/A</span>
                                </p>
                            </div>
                        </div>

                        <div v-if="hasUserKitDetails">
                            <h4 @click="toggleUserKitAccordion" class="accordion-header">
                                Kit Details
                                <span>{{ isUserKitAccordionOpen ? '▲' : '▼' }}</span>
                            </h4>
                            <div v-if="isUserKitAccordionOpen" class="accordion-content">
                                <ul class="kit-list">
                                    <li v-for="kit in (log.user_data ? log.user_data.testkits : log.userData.testkits)"
                                        :key="kit.id" class="kit-item">
                                        <p v-if="kit.kit_id"><strong>Kit ID:</strong> {{ convertKitId(kit.kit_id) }}</p>
                                        <p v-if="kit.title"><strong>Title:</strong> {{ kit.title || 'N/A' }}</p>
                                        <p v-if="kit.status"><strong>Status:</strong> {{ kit.status || 'N/A' }}</p>
                                        <p v-if="kit.city || kit.state">
                                            <strong>Location:</strong>
                                            {{ kit.city || 'N/A' }}, {{ kit.state || 'N/A' }}
                                        </p>
                                        <p v-if="kit.sampling_date">
                                            <strong>Sampling Date:</strong> {{ formatDate(kit.sampling_date) }}
                                        </p>
                                        <div v-if="kit.indoorSamples && kit.indoorSamples.length">
                                            <h4 @click="toggleKitSamples(kit.id)" class="accordion-header">
                                                Indoor Samples
                                                <span>{{ isKitSamplesOpen(kit.id) ? '▲' : '▼' }}</span>
                                            </h4>
                                            <div v-if="isKitSamplesOpen(kit.id)" class="accordion-content">
                                                <ul class="kit-list">
                                                    <li v-for="sample in kit.indoorSamples" :key="sample.id"
                                                        class="kit-item">
                                                        <p v-if="sample.id"><strong>ID:</strong> {{ sample.id }}</p>
                                                        <p v-if="sample.type"><strong>Type:</strong> {{ sample.type ||
                                                            'N/A' }}</p>
                                                        <p v-if="sample.created_at"><strong>Date:</strong> {{
                                                            formatDate(sample.created_at) }}</p>
                                                        <p v-if="sample.sample_order !== null"><strong>Sample
                                                                Order:</strong> {{ sample.sample_order }}</p>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    <section class="log-section" v-if="hasTestKitData">
                        <h3>Test Kit Data</h3>
                        <p v-if="log.test_kit_data.kit_id">
                            <strong>Kit ID:</strong> {{ convertKitId(log.test_kit_data.kit_id) }}
                        </p>
                        <p v-if="log.test_kit_data.title">
                            <strong>Title:</strong> {{ log.test_kit_data.title || 'N/A' }}
                        </p>
                        <p v-if="log.test_kit_data.status">
                            <strong>Status:</strong> {{ log.test_kit_data.status || 'N/A' }}
                        </p>
                        <p v-if="log.test_kit_data.city || log.test_kit_data.state">
                            <strong>Location:</strong>
                            {{ log.test_kit_data.city || 'N/A' }}, {{ log.test_kit_data.state || 'N/A' }}
                        </p>
                        <p v-if="log.test_kit_data.sampling_date">
                            <strong>Sampling Date:</strong>
                            {{ formatDate(log.test_kit_data.sampling_date) }}
                        </p>

                        <div v-if="log.test_kit_data.indoorSamples && log.test_kit_data.indoorSamples.length">
                            <h4 @click="toggleTestKitSamplingAccordion" class="accordion-header">
                                Indoor Samples
                                <span>{{ isTestKitSamplingAccordionOpen ? '▲' : '▼' }}</span>
                            </h4>
                            <div v-if="isTestKitSamplingAccordionOpen" class="accordion-content">
                                <ul class="kit-list">
                                    <li v-for="sample in log.test_kit_data.indoorSamples" :key="sample.id"
                                        class="kit-item">
                                        <p v-if="sample.id"><strong>ID:</strong> {{ sample.id }}</p>
                                        <p v-if="sample.type"><strong>Type:</strong> {{ sample.type || 'N/A' }}</p>
                                        <p v-if="sample.created_at">
                                            <strong>Date:</strong> {{ formatDate(sample.created_at) }}
                                        </p>
                                        <p v-if="sample.sample_order !== null">
                                            <strong>Sample Order:</strong> {{ sample.sample_order }}
                                        </p>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    <section class="log-section">
                        <h3>Action & Response</h3>
                        <p v-if="safeGet(log, 'log_type') !== 'Unknown'">
                            <strong>Action:</strong> {{ safeGet(log, 'log_type') }}
                        </p>
                        <p v-if="(safeGet(log, 'response_status') || safeGet(log, 'response.status')) !== 'Unknown'">
                            <strong>Response Status:</strong>
                            {{ getResponseStatusExplanation(safeGet(log, 'response_status')) }}
                        </p>
                    </section>

                    <section class="log-section"
                        v-if="(safeGet(log, 'ua_details.request.url', log.request && log.request.url)) !== 'Unknown' ||
                            (safeGet(log, 'ua_details.request.method', log.request && log.request.method)) !== 'Unknown' ||
                            (safeGet(log, 'ua_details.request.referrer', log.request && log.request.referrer)) !== 'Unknown'">
                        <h3>Request Details</h3>
                        <p v-if="safeGet(log, 'ua_details.request.url', log.request && log.request.url) !== 'Unknown'">
                            <strong>URL:</strong>
                            {{ safeGet(log, 'ua_details.request.url', log.request && log.request.url) }}
                        </p>
                        <p
                            v-if="safeGet(log, 'ua_details.request.method', log.request && log.request.method) !== 'Unknown'">
                            <strong>Method:</strong>
                            {{ safeGet(log, 'ua_details.request.method', log.request && log.request.method) }}
                        </p>
                        <p
                            v-if="safeGet(log, 'ua_details.request.referrer', log.request && log.request.referrer) !== 'Unknown'">
                            <strong>Referrer:</strong>
                            {{ safeGet(log, 'ua_details.request.referrer', log.request && log.request.referrer) }}
                        </p>
                    </section>

                    <section class="log-section">
                        <h3>Device & Browser</h3>
                        <p v-if="safeGet(log, 'ua_details.ip', log.ip) !== 'Unknown'">
                            <strong>IP Address:</strong>
                            {{ safeGet(log, 'ua_details.ip', log.ip) }}
                        </p>
                        <p v-if="formatOS() !== 'Unknown OS'"><strong>OS:</strong> {{ formatOS() }}</p>
                        <p v-if="formatBrowser() !== 'Unknown Browser'"><strong>Browser:</strong> {{ formatBrowser() }}
                        </p>
                        <p v-if="safeGet(log, 'ua_details.device.type', log.device && log.device.type) !== 'Unknown'">
                            <strong>Device:</strong>
                            {{ safeGet(log, 'ua_details.device.type', log.device && log.device.type) }}
                        </p>
                        <p
                            v-if="safeGet(log, 'ua_details.cpu.architecture', log.cpu && log.cpu.architecture) !== 'Unknown'">
                            <strong>CPU:</strong>
                            {{ safeGet(log, 'ua_details.cpu.architecture', log.cpu && log.cpu.architecture) }}
                        </p>
                        <p v-if="formatEngine() !== 'Unknown Engine'">
                            <strong>Engine:</strong> {{ formatEngine() }}
                        </p>
                    </section>

                    <section class="log-section" v-if="formatLocation() !== 'Unknown Location'">
                        <h3>Location</h3>
                        <p><strong>Location:</strong> {{ formatLocation() }}</p>
                    </section>

                    <section class="log-section" v-if="(safeGet(log, 'request.url')) !== 'Unknown' ||
                        (safeGet(log, 'request.method')) !== 'Unknown' ||
                        (safeGet(log, 'request.referrer')) !== 'Unknown'">
                        <h3>Request Details</h3>
                        <p v-if="safeGet(log, 'request.url') !== 'Unknown'">
                            <strong>URL:</strong> {{ safeGet(log, 'request.url') }}
                        </p>
                        <p v-if="safeGet(log, 'request.method') !== 'Unknown'">
                            <strong>Method:</strong> {{ safeGet(log, 'request.method') }}
                        </p>
                        <p v-if="safeGet(log, 'request.referrer') !== 'Unknown'">
                            <strong>Referrer:</strong> {{ safeGet(log, 'request.referrer') }}
                        </p>
                    </section>

                    <section class="log-section" v-if="(safeGet(log, 'created_at', safeGet(log, 'timestamp')) !== 'Unknown') ||
                        (safeGet(log, 'timezone') !== 'Unknown')">
                        <h3>Timestamp</h3>
                        <p v-if="safeGet(log, 'created_at', safeGet(log, 'timestamp')) !== 'Unknown'">
                            <strong>Date:</strong>
                            {{ formatDate(safeGet(log, 'created_at', safeGet(log, 'timestamp'))) }}
                        </p>
                        <p v-if="safeGet(log, 'timezone') !== 'Unknown'">
                            <strong>Timezone:</strong>
                            {{ safeGet(log, 'timezone') }}
                        </p>
                    </section>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import moment from "moment";
import { parseAndConvertKitId } from "../mixins/parseAndConvertKitId";

export default {
    mixins: [parseAndConvertKitId],
    props: {
        show: Boolean,
        log: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            isUserAccordionOpen: false,
            isUserKitAccordionOpen: false,
            isTestKitSamplingAccordionOpen: false,
            openKitSamples: {}
        };
    },
    computed: {
        hasUserDetails() {
            const uid = this.safeGet(this.log, "user_id", null);
            return uid && uid !== "Unknown";
        },
        hasUserAdditionalDetails() {
            const phone =
                this.safeGet(this.log, "user_data.phone", this.safeGet(this.log, "userData.phone", null));
            const roles =
                (this.log.user_data &&
                    this.log.user_data.roles &&
                    this.log.user_data.roles.length) ||
                (this.log.userData &&
                    this.log.userData.roles &&
                    this.log.userData.roles.length);
            return (phone && phone !== "Unknown") || roles;
        },
        hasUserKitDetails() {
            const kits =
                (this.log.user_data && this.log.user_data.testkits) ||
                (this.log.userData && this.log.userData.testkits);
            return kits && kits.length > 0;
        },
        hasTestKitData() {
            return this.log.test_kit_data && Object.keys(this.log.test_kit_data).length > 0;
        }
    },
    methods: {
        getResponseStatusExplanation(status) {
            const responseStatusMap = {
                200: "200 ( OK )",
                201: "201 ( Created )",
                204: "204 ( No Content )",
                400: "400 ( Bad Request )",
                401: "401 ( Unauthorized )",
                403: "403 ( Forbidden )",
                404: "404 ( Not Found )",
                500: "500 ( Internal Server Error )",
                502: "502 ( Bad Gateway )",
                503: "503 ( Service Unavailable )"
            };

            return responseStatusMap[status] || `${status}: Unknown Status`;
        },
        close() {
            this.$emit("close");
        },
        formatDate(date) {
            return date ? moment(date).format("MM/DD/YYYY hh:mm:ss A") : "-";
        },
        safeGet(obj, path, defaultValue = "Unknown") {
            return path.split(".").reduce(
                (acc, key) => (acc && acc[key] !== undefined ? acc[key] : defaultValue),
                obj
            );
        },
        formatLocation() {
            const loc = this.safeGet(this.log, "ua_details.location", this.log.location || {});
            if (typeof loc === "object" && loc !== null) {
                const { city, region, country } = loc;
                const parts = [];
                if (city && city !== "Unknown") parts.push(city);
                if (region && region !== "Unknown") parts.push(region);
                if (country && country !== "Unknown") parts.push(country);
                return parts.length ? parts.join(", ") : "Unknown Location";
            }
            return loc || "Unknown Location";
        },
        formatOS() {
            const os = this.safeGet(this.log, "ua_details.os", this.log.os || {});
            if (os && os.name) {
                return os.version ? `${os.name} (${os.version})` : os.name;
            }
            return "Unknown OS";
        },
        formatBrowser() {
            const browser = this.safeGet(this.log, "ua_details.browser", this.log.browser || {});
            if (browser && browser.name) {
                return browser.version ? `${browser.name} (${browser.version})` : browser.name;
            }
            return "Unknown Browser";
        },
        formatEngine() {
            const engine = this.safeGet(this.log, "ua_details.engine", this.log.engine || {});
            if (engine && engine.name) {
                return engine.version ? `${engine.name} (${engine.version})` : engine.name;
            }
            return "Unknown Engine";
        },
        toggleUserAccordion() {
            this.isUserAccordionOpen = !this.isUserAccordionOpen;
        },
        toggleUserKitAccordion() {
            this.isUserKitAccordionOpen = !this.isUserKitAccordionOpen;
        },
        toggleTestKitSamplingAccordion() {
            this.isTestKitSamplingAccordionOpen = !this.isTestKitSamplingAccordionOpen;
        },
        toggleKitSamples(kitId) {
            this.$set(this.openKitSamples, kitId, !this.openKitSamples[kitId]);
        },
        isKitSamplesOpen(kitId) {
            return !!this.openKitSamples[kitId];
        },
    },
    watch: {
        show(value) {
            document.body.style.overflow = value ? "hidden" : "";
        }
    },
    beforeDestroy() {
        this.isUserAccordionOpen = false;
        this.isUserKitAccordionOpen = false;
        this.isTestKitSamplingAccordionOpen = false;
        document.body.style.overflow = "";
    }
};
</script>

<style scoped>
.accordion-header {
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #e9ecef;
    padding: 10px;
    border-radius: 5px;
    font-size: 16px;
    color: #007bff;
}

.accordion-content {
    padding: 10px;
    background: #f8f9fa;
    border-radius: 5px;
    margin-top: 5px;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-container {
    background: #ffffff;
    padding: 20px;
    border-radius: 8px;
    width: 600px;
    max-height: 80vh;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
    font-family: "TTNormsLight", serif;
    overflow-y: auto;
}

.modal-content {
    max-height: 60vh;
    overflow-y: auto;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #ddd;
    padding-bottom: 10px;
    margin-bottom: 15px;
}

.modal-header h2 {
    font-size: 20px;
    color: #007bff;
}

.close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
}

.log-section {
    margin-bottom: 15px;
    padding: 10px;
    background: #f8f9fa;
    border-radius: 5px;
}

.log-section h3 {
    font-size: 16px;
    color: #007bff;
    margin-bottom: 5px;
}

.log-section h4 {
    font-size: 14px;
    color: #007bff;
    margin-bottom: 5px;
    padding-left: 10px;
}

p {
    font-size: 14px;
    margin: 5px 0;
}

.kit-list {
    list-style: none;
    padding: 0;
}

.kit-item {
    background: #ffffff;
    padding: 8px;
    margin-bottom: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.kit-item p {
    margin: 4px 0;
    font-size: 13px;
    color: #555;
}
</style>